import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Grid } from "@material-ui/core";
import Empty from "antd/es/empty";
import { InfoTable } from "../../components";
import { getClientById } from "../../redux/action/clientAction";
import { EMPLOYEE_ACTION } from "../../redux/action";
import { renderMeetingLink } from "../../utils/meetingLink";
import "./my_tax_team.scss";

function parseAssignedEmployeeIds(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
    } catch {
      /* fall through */
    }
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeEmployeeRecord(payload) {
  if (payload == null) return null;
  if (Array.isArray(payload)) {
    const first = payload[0];
    return first && typeof first === "object" ? first : null;
  }
  return typeof payload === "object" ? payload : null;
}

function formatEmployeeName(employee) {
  if (!employee) return "—";
  const name = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return name || "—";
}

function formatMeetingLink(link) {
  return renderMeetingLink(link, {
    emptyText: "—",
    className: "tax-team-meeting-link",
  });
}

function buildMemberInfo(employee) {
  return {
    name: formatEmployeeName(employee),
    email: employee?.email || "—",
    phone: employee?.phone || "—",
    designation: employee?.designation || employee?.role || "—",
    meetingLink: formatMeetingLink(employee?.meeting_link),
  };
}

const MEMBER_HEADINGS = [
  "Name",
  "Email",
  "Phone",
  "Designation",
  "Meeting Link",
];
const MEMBER_KEYS = ["name", "email", "phone", "designation", "meetingLink"];

const MyTaxTeam = () => {
  const dispatch = useDispatch();
  const clientId = localStorage.getItem("user_id");
  const [loading, setLoading] = useState(true);
  const [manager, setManager] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadTaxTeam = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const client = await dispatch(getClientById(clientId)).unwrap();
        if (cancelled || !client) {
          setManager(null);
          setTeamMembers([]);
          return;
        }

        const managerId = client.sales ? String(client.sales) : null;
        const assignedIds = parseAssignedEmployeeIds(client.assigned_employee_ids);

        const fetchEmployee = async (employeeId) => {
          try {
            const payload = await dispatch(
              EMPLOYEE_ACTION.getEmployee(String(employeeId)),
            ).unwrap();
            return normalizeEmployeeRecord(payload);
          } catch {
            return null;
          }
        };

        const [managerRecord, ...assignedRecords] = await Promise.all([
          managerId ? fetchEmployee(managerId) : Promise.resolve(null),
          ...assignedIds.map((id) => fetchEmployee(id)),
        ]);

        if (cancelled) return;

        setManager(managerRecord);

        const memberMap = new Map();
        assignedRecords.forEach((record, index) => {
          if (!record) return;
          const id =
            record.employee_id ??
            record.employeeID ??
            assignedIds[index];
          if (!id) return;
          if (managerId && String(id) === managerId) return;
          memberMap.set(String(id), record);
        });

        setTeamMembers(Array.from(memberMap.values()));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadTaxTeam();
    return () => {
      cancelled = true;
    };
  }, [dispatch, clientId]);

  const hasTeam = Boolean(manager) || teamMembers.length > 0;

  return (
    <div className="my-tax-team-page">
      <div className="my-tax-team-page-content">
        {loading ? (
          <p className="my-tax-team-loading">Loading your tax team...</p>
        ) : !hasTeam ? (
          <div className="my-tax-team-empty">
            <Empty description="No tax team members assigned yet" />
          </div>
        ) : (
          <Grid container spacing={3}>
            {manager && (
              <Grid item xs={12} md={6}>
                <div className="my-tax-team-card">
                  <h3 className="my-tax-team-card-title">Client Manager</h3>
                  <InfoTable
                    transparent
                    titles={MEMBER_HEADINGS.map((h) => `${h}:`)}
                    keys={MEMBER_KEYS}
                    info={buildMemberInfo(manager)}
                  />
                </div>
              </Grid>
            )}
            {teamMembers.map((member) => {
              const memberId =
                member.employee_id ??
                member.employeeID ??
                formatEmployeeName(member);
              return (
                <Grid item xs={12} md={6} key={`tax-team-${memberId}`}>
                  <div className="my-tax-team-card">
                    <h3 className="my-tax-team-card-title">Team Member</h3>
                    <InfoTable
                      transparent
                      titles={MEMBER_HEADINGS.map((h) => `${h}:`)}
                      keys={MEMBER_KEYS}
                      info={buildMemberInfo(member)}
                    />
                  </div>
                </Grid>
              );
            })}
          </Grid>
        )}
      </div>
    </div>
  );
};

export default MyTaxTeam;
